import SparkMD5 from "spark-md5";

import { useFile } from "./queries/useFile";

const chunkSize = 2097152; // 2MB

// this is straight from sparkMD5 doc
const checksum = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const chunkCount = Math.ceil(file.size / chunkSize);
    let chunkIndex = 0;
    const md5buffer = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();

    const readNextChunk = () => {
      if (chunkIndex < chunkCount || (chunkIndex === 0 && chunkCount === 0)) {
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const bytes = file.slice(start, end);
        fileReader.readAsArrayBuffer(bytes);
        chunkIndex++;
        return true;
      } else {
        return false;
      }
    };

    fileReader.addEventListener("load", (event) => {
      if (!event.target?.result) {
        return;
      }

      md5buffer.append(event.target.result as ArrayBuffer);

      if (!readNextChunk()) {
        const binaryDigest = md5buffer.end(true);
        const base64digest = btoa(binaryDigest);
        resolve(base64digest);
      }
    });

    fileReader.addEventListener("error", (event) => {
      reject(event);
    });

    readNextChunk();
  });
};

export const useDirectUpload = () => {
  const { useCreateFileUploadUrl, useUploadFileToGCS } = useFile();

  const { mutateAsync: createFileUploadUrlMutation } = useCreateFileUploadUrl();
  const { mutateAsync: uploadFileToGCSMutation } = useUploadFileToGCS();

  const createFileUploadUrl = async (key: string, f: File) => {
    const res = await createFileUploadUrlMutation({
      key,
      fileName: f.name,
      size: f.size,
      contentType: f.type,
      hash: await checksum(f),
    });
    if (res.status === 201) {
      return res.data;
    } else {
      return Promise.reject();
    }
  };

  const uploadFile = async (
    file: File,
    uploadDetails: Awaited<ReturnType<typeof createFileUploadUrl>>,
    onProgress?: (percentage: number) => void
  ) => {
    try {
      await uploadFileToGCSMutation({
        url: uploadDetails?.upload.url,
        file,
        headers: uploadDetails.upload.headers as Record<string, string>,
        onProgress: (percentage: number) => {
          if (onProgress) {
            onProgress(percentage);
          }
        },
      });

      // if the file is uploaded correctly then you'll get a fileSignedId
      // you'll want to persist it for the backend
      return uploadDetails.fileSignedId;
    } catch (e) {
      console.error("Can t create a direct upload", e);

      throw e;
    }
  };

  return { uploadFile, createFileUploadUrl };
};
